import React, { useState, useEffect } from 'react';
import { apiService, Device } from '../services/api';
import { Monitor, Smartphone, Server, Apple, Tablet, Laptop, RefreshCw, Box, Scan } from 'lucide-react';
import AndroidManagement from './AndroidManagement';
import LinuxManagement from './LinuxManagement';
import IOSManagement from './iOSManagement';
import MacOSManagement from './macOSManagement';
import DirectoryView from './DirectoryView';
import { DepartmentCollectionDropdown } from './DepartmentCollectionDropdown';
import { HubPage } from './ui/HubShell';
import { DeviceIdentityCell } from './ui/DeviceIdentityCell';

export type DevicePlatformFilter = 'all' | 'windows' | 'linux' | 'android' | 'ios' | 'macos';

function getDevicePlatform(os: string | undefined): DevicePlatformFilter {
  if (!os) return 'all';
  const lower = os.toLowerCase();
  if (lower.includes('windows')) return 'windows';
  if (lower.includes('linux') || lower.includes('ubuntu') || lower.includes('debian') || lower.includes('rhel') || lower.includes('centos')) return 'linux';
  if (lower.includes('android')) return 'android';
  if (lower.includes('ios') || lower.includes('iphone')) return 'ios';
  if (lower.includes('mac') || lower.includes('darwin')) return 'macos';
  return 'all';
}

const DEVICE_TYPE_CARDS = [
  { id: 'windows' as const, name: 'Windows', icon: Monitor, topBorder: 'border-t-blue-500', description: 'Windows devices' },
  { id: 'linux' as const, name: 'Linux', icon: Box, topBorder: 'border-t-orange-500', description: 'Linux systems' },
  { id: 'macos' as const, name: 'macOS', icon: Apple, topBorder: 'border-t-gray-400', description: 'macOS devices' },
  { id: 'android' as const, name: 'Android', icon: Smartphone, topBorder: 'border-t-green-500', description: 'Android device management' },
  { id: 'ios' as const, name: 'iOS', icon: Smartphone, topBorder: 'border-t-blue-300', description: 'iOS devices' },
  { id: 'all' as const, name: 'All devices', icon: Laptop, topBorder: 'border-t-slate-400', description: 'All endpoints across platforms' },
];

const deviceDept = (d: Device) => (d as any).dept || (d as any).department || 'Unassigned';

interface DevicesHubProps {
  selectedOU?: string | null;
  viewingDirectory?: boolean;
}

const DevicesHub: React.FC<DevicesHubProps> = ({ selectedOU: initialOU, viewingDirectory }) => {
  const [selectedPlatform, setSelectedPlatform] = useState<DevicePlatformFilter | null>(null);
  const [devices, setDevices] = useState<Device[]>([]);
  const [groupDevices, setGroupDevices] = useState<Device[] | null>(null);
  const [collectionFilter, setCollectionFilter] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [scanningDeviceId, setScanningDeviceId] = useState<string | null>(null);
  const [scanMessage, setScanMessage] = useState<{ deviceId: string; type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    loadDevices();
  }, []);


  useEffect(() => {
    if (!collectionFilter || collectionFilter.startsWith('dept-') || collectionFilter === 'Unassigned' || collectionFilter.startsWith('dept-custom-') || collectionFilter.startsWith('folder-')) {
      setGroupDevices(null);
      return;
    }
    let c = false;
    apiService.getGroupDevices(collectionFilter)
      .then((r) => { if (!c) setGroupDevices(r.items || []); })
      .catch(() => { if (!c) setGroupDevices([]); });
    return () => { c = true; };
  }, [collectionFilter]);

  const loadDevices = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiService.getDevices();
      setDevices(data || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load devices');
    } finally {
      setLoading(false);
    }
  };

  const scopeDevices = (() => {
    if (!collectionFilter) return devices;
    if (collectionFilter.startsWith('folder-')) return devices;
    if (groupDevices) return groupDevices;
    return devices.filter((d) => deviceDept(d) === collectionFilter);
  })();

  const countByPlatform = (id: DevicePlatformFilter) => {
    if (id === 'all') return scopeDevices.length;
    return scopeDevices.filter((d) => getDevicePlatform(d.os) === id).length;
  };

  const filteredDevices = selectedPlatform
    ? scopeDevices.filter((d) => selectedPlatform === 'all' || getDevicePlatform(d.os) === selectedPlatform)
    : [];

  const handleScanInventory = async (device: Device) => {
    setScanningDeviceId(device.device_id);
    setScanMessage(null);
    try {
      await apiService.triggerDeviceInventorySync(device.device_id, ['hardware', 'software']);
      setScanMessage({
        deviceId: device.device_id,
        type: 'success',
        text: `Scan started for ${device.name || device.device_id}. Inventory will update shortly.`,
      });
    } catch (err) {
      setScanMessage({
        deviceId: device.device_id,
        type: 'error',
        text: err instanceof Error ? err.message : 'Failed to trigger inventory scan',
      });
    } finally {
      setScanningDeviceId(null);
    }
  };

  if (viewingDirectory) {
    return <DirectoryView initialSelectedId={initialOU} />;
  }

  return (
    <HubPage
      title="Devices"
      subtitle="Browse enrolled endpoints by platform, or filter by collection."
      icon={<Laptop size={20} />}
      eyebrow="Fleet"
      action={
        <div className="flex items-center gap-2 flex-wrap">
          <DepartmentCollectionDropdown
            devices={devices}
            value={collectionFilter}
            onChange={(id) => setCollectionFilter(id)}
          />
          <button
            type="button"
            onClick={loadDevices}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-50"
          >
            {loading ? (
              <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <RefreshCw size={16} />
            )}
            Refresh
          </button>
        </div>
      }
    >
      {/* OS category cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-6 gap-4">
        {DEVICE_TYPE_CARDS.map((card) => {
          const Icon = card.icon;
          const count = countByPlatform(card.id);
          const isSelected = selectedPlatform === card.id;

          return (
            <div
              key={card.id}
              onClick={() => setSelectedPlatform(isSelected ? null : card.id)}
              className={`bg-white border-t-4 ${card.topBorder} border border-slate-200 rounded-xl p-6 cursor-pointer transition-all hover:shadow-lg shadow-sm ${
                isSelected ? 'ring-2 ring-offset-2 ring-blue-500' : ''
              }`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="p-2.5 rounded-lg bg-slate-50">
                  <Icon className="w-8 h-8 text-slate-600" />
                </div>
                <span className="text-2xl font-bold text-slate-800">{count}</span>
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-1">{card.name}</h3>
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">{card.description}</p>
            </div>
          );
        })}
      </div>

      {/* Selected platform content */}
      {selectedPlatform === 'android' && (
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
          <AndroidManagement />
        </div>
      )}
      {selectedPlatform === 'linux' && (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          <LinuxManagement />
        </div>
      )}
      {selectedPlatform === 'ios' && (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          <IOSManagement />
        </div>
      )}
      {selectedPlatform === 'macos' && (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          <MacOSManagement />
        </div>
      )}
      {(selectedPlatform === 'all' || selectedPlatform === 'windows') && (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200">
          <div className="p-4 border-b border-slate-200 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-slate-800">
              {selectedPlatform === 'all' ? 'All devices' : 'Windows devices'} ({filteredDevices.length})
            </h3>
          </div>
          <div className="p-4">
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                {error}
              </div>
            )}
            {loading ? (
              <div className="flex items-center gap-2 py-8 text-slate-500">
                <span className="inline-block w-5 h-5 border-2 border-slate-400 border-t-transparent rounded-full animate-spin" />
                Loading devices...
              </div>
            ) : (
              <div className="overflow-x-auto">
                {scanMessage && (
                  <div
                    className={`mb-4 p-3 rounded-lg text-sm ${
                      scanMessage.type === 'success'
                        ? 'bg-green-50 border border-green-200 text-green-700'
                        : 'bg-red-50 border border-red-200 text-red-700'
                    }`}
                  >
                    {scanMessage.text}
                  </div>
                )}
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 text-left text-slate-600 font-medium">
                      <th className="py-3 px-2">Device Name</th>
                      <th className="py-3 px-2">OS</th>
                      <th className="py-3 px-2">Platform</th>
                      <th className="py-3 px-2">Status</th>
                      <th className="py-3 px-2">Compliance</th>
                      <th className="py-3 px-2 w-24">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredDevices.map((device) => (
                      <tr key={device.device_id} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="py-3 px-2 font-medium text-slate-800">
                          <DeviceIdentityCell device={device} fallbackId={device.device_id} />
                        </td>
                        <td className="py-3 px-2 text-slate-600">{device.os || '—'}</td>
                        <td className="py-3 px-2">
                          <span className="px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-700">
                            {device.platform || '—'}
                          </span>
                        </td>
                        <td className="py-3 px-2">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            device.status === 'online' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                          }`}>
                            {device.status}
                          </span>
                        </td>
                        <td className="py-3 px-2">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            device.compliance_status === 'compliant' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                          }`}>
                            {device.compliance_status}
                          </span>
                        </td>
                        <td className="py-3 px-2">
                          <button
                            type="button"
                            onClick={() => handleScanInventory(device)}
                            disabled={scanningDeviceId === device.device_id}
                            className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-blue-600 hover:bg-blue-50 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Scan hardware and software inventory"
                          >
                            {scanningDeviceId === device.device_id ? (
                              <span className="inline-block w-3 h-3 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                            ) : (
                              <Scan size={14} />
                            )}
                            Scan
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {filteredDevices.length === 0 && (
                  <p className="py-8 text-center text-slate-500">No devices found. Enroll devices to see them here.</p>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </HubPage>
  );
};

export default DevicesHub;
